/**
 * @file axios.js
 * @description Centralized Axios HTTP API Client configuration and interceptors.
 */
import axios from "axios";
import { 
  encryptPayload, 
  decryptPayload, 
  getSessionId, 
  clearSession
} from "../utils/cryptoUtils";

// ── Environment URL Map ──────────────────────────────────────────────────────
const API_URLS = {
  live:  'https://api.securecheckapp.us',
  stage: 'https://apidemo.securecheckapp.us',
};

// Resolve the base URL
const REACT_APP_ENV = process.env.REACT_APP_ENV || 'live';
const forceLiveApi = process.env.REACT_APP_FORCE_LIVE_API === 'true';
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

/**
 * Resolved base API URL based on current environment and window location.
 * @type {string}
 */
export const apiBaseUrl = (isLocal && !forceLiveApi)
  ? 'http://localhost:5000'
  : (API_URLS[REACT_APP_ENV] || API_URLS.live);

const BASE_URL = apiBaseUrl;

/**
 * Configured Axios instance with authentication token and response interceptors.
 */
const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Perform ECDH Handshake (Disabled — crypto exchange not required)
export const initializeSecureSession = async () => Promise.resolve();

api.interceptors.request.use(
  async (config) => {
    // Attach Bearer token from localStorage if present
    const token = localStorage.getItem('token');
    if (token) {
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set('Authorization', `Bearer ${token}`);
      } else if (config.headers) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const sessionId = getSessionId();
    if (sessionId) {
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set('X-Session-Id', sessionId);
      } else {
        config.headers['X-Session-Id'] = sessionId;
      }
    }

    // Encrypt JSON body for POST/PUT/PATCH (skip FormData requests)
    const isFormData = config.data instanceof FormData;
    if (isFormData) {
      if (config.headers) {
        if (typeof config.headers.delete === 'function') {
          config.headers.delete('Content-Type');
          config.headers.delete('content-type');
        } else {
          delete config.headers['Content-Type'];
          delete config.headers['content-type'];
        }
      }
    } else if (config.data && ['post', 'put', 'patch'].includes(config.method) && sessionId) {
      config._plainData = config.data; // Save original for potential retry
      config.data = await encryptPayload(config.data);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  async (response) => {
    if (response.data && response.data.encrypted) {
      response.data = await decryptPayload(response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 400 && error.response.data?.message === "Decryption failed or session expired" && !originalRequest._retry) {
      originalRequest._retry = true;
      clearSession();
      await initializeSecureSession(true); // force=true to bypass "already initialized" guard
      
      // Restore the plain data so the request interceptor can re-encrypt it with the new session
      if (originalRequest._plainData) {
        originalRequest.data = originalRequest._plainData;
      }
      return api(originalRequest);
    }

    if (error.response?.status === 401) {
      const isLoginPage = window.location.pathname === "/login";
      // Don't redirect if the 401 came from the silent token-restore refresh call
      // (that's expected to fail when user hasn't logged in yet)
      const isRefreshCall = originalRequest?.url?.includes('/auth/refresh');
      if (!isLoginPage && !isRefreshCall) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);

export default api;

